import React, { useEffect, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Space,
  Typography,
  Popconfirm,
  Tag,
  Empty,
  Badge,
  Tooltip,
  Select,
  Modal,
  Form,
  Divider,
  Upload,
  message,
  Alert,
  Table
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  SearchOutlined,
  IdcardOutlined,
  BranchesOutlined,
  KeyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
  ImportOutlined,
  NumberOutlined,
  CalendarOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { getRequest, postRequest, deleteRequestNewApi } from "../../../Utils/AppApi";
import * as XLSX from 'xlsx';
import { CSVLink } from "react-csv";
import moment from "moment";

import { 
  GET_ALL_BANKS, 
  SEARCH_BANK_BY_IFSC, 
  FILTER_BANK_BY_KEY, 
  GET_UNIQUE_KEYS, 
  SAVE_BANK, 
  UPDATE_BANK, 
  REMOVE_BANK,
  EXPORT_BANKS,
  BULK_IMPORT_BANKS
} from './../../../Utils/appConstants';

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const BankManagementPage = ({ history }) => {
  const [form] = Form.useForm();
  const csvLinkRef = useRef(null);
  
  // Data states
  const [banks, setBanks] = useState([]);
  const [uniqueKeys, setUniqueKeys] = useState([]);
  const [editingBank, setEditingBank] = useState(null);
  const [exportData, setExportData] = useState([]);

  // UI states
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("name");
  const [selectedKey, setSelectedKey] = useState("");
  
  // Modal states
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState("Add New Bank");
  
  // Import states
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  
  // Pagination states
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
    pages: 1
  });

  /** FETCH BANKS with pagination */
  const fetchBanks = useCallback(() => {
    setLoading(true);
    const params = {
      page: pagination.current,
      limit: pagination.pageSize,
      ...(searchTerm && { search: searchTerm }),
      ...(selectedKey && { key: selectedKey }),
      sortBy: 'BankID',
      sortOrder: 'desc'
    };
    
    const queryString = new URLSearchParams(params).toString();
    
    getRequest(`${GET_ALL_BANKS}?${queryString}`, history)
      .then(res => {
        if (res?.success) {
          setBanks(res.data || []);
          if (res.pagination) {
            setPagination({
              ...pagination,
              total: res.pagination.total,
              pages: res.pagination.pages
            });
          }
        }
      })
      .catch(err => {
        toast.error("Failed to fetch banks");
        console.error("Error fetching banks:", err);
      })
      .finally(() => setLoading(false));
  }, [history, pagination.current, pagination.pageSize, searchTerm, selectedKey]);

  /** FETCH UNIQUE KEYS */
  const fetchUniqueKeys = useCallback(() => {
    getRequest(GET_UNIQUE_KEYS, history)
      .then(res => {
        if (res?.success) setUniqueKeys(res.data || []);
      })
      .catch(err => {
        console.error("Failed to fetch unique keys:", err);
      });
  }, [history]);

  /** HANDLE TABLE PAGINATION */
  const handleTableChange = (page, totalRows) => {
    setPagination(prev => ({
      ...prev,
      current: page
    }));
  };

  /** HANDLE PER PAGE CHANGE */
  const handlePerRowsChange = (currentRowsPerPage, currentPage) => {
    setPagination(prev => ({
      ...prev,
      pageSize: currentRowsPerPage,
      current: currentPage
    }));
  };

  /** SEARCH BANKS */
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  /** FILTER BY KEY */
  const handleFilterByKey = (key) => {
    setSelectedKey(key);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  /** OPEN ADD MODAL */
  const openAddModal = () => {
    setEditingBank(null);
    setModalTitle("Add New Bank");
    form.resetFields();
    form.setFieldsValue({ 
      status: 'active',
      BankID: '' // Clear BankID for new entry
    });
    setModalVisible(true);
  };

  /** OPEN EDIT MODAL */
  const openEditModal = (bank) => {
    setEditingBank(bank);
    setModalTitle("Edit Bank");
    form.setFieldsValue({
      BankID: bank.BankID || "",
      BANK_NAME: bank.BANK_NAME || "",
      ifsc: bank.ifsc || "",
      branchName: bank.branchName || "",
      key: bank.key || "",
      bankName: bank.bankName || "",
      status: bank.status || "active"
    });
    setModalVisible(true);
  };

  /** CLOSE MODAL */
  const closeModal = () => {
    setModalVisible(false);
    setEditingBank(null);
    form.resetFields();
  };

  /** SAVE / UPDATE BANK */
  const handleSave = async (values) => {
    if (!values.BANK_NAME?.trim()) return toast.error("Bank name is required");
    if (!values.ifsc?.trim()) return toast.error("IFSC code is required");
    
    // Validate IFSC format
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(values.ifsc)) {
      return toast.error("Invalid IFSC code format. Example: SBIN0001234");
    }

    // Validate BankID for new entries (optional)
    if (!editingBank && values.BankID) {
      if (isNaN(values.BankID)) {
        return toast.error("Bank ID must be a number");
      }
      if (values.BankID <= 0) {
        return toast.error("Bank ID must be a positive number");
      }
    }

    setActionLoading(true);

    try {
      const url = editingBank ? `${UPDATE_BANK}/${editingBank._id}` : SAVE_BANK;
      const method = editingBank ? "PUT" : "POST";
      
      // Include BankID in payload for new entries
      const payload = editingBank ? values : { ...values, BankID: values.BankID || null };
      
      const res = await postRequest(url, payload, history, method);
      
      if (res?.success) {
        toast.success(res.message || (editingBank ? "Bank updated successfully" : "Bank added successfully"));
        closeModal();
        fetchBanks();
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  /** REMOVE BANK */
  const handleRemove = async (id) => {
    setRemovingId(id);
    try {
      const res = await deleteRequestNewApi(`${REMOVE_BANK}/${id}`, history);
      if (res?.success) {
        toast.success("Bank removed successfully");
        fetchBanks();
      } else {
        toast.error(res?.message);
      }
    } catch {
      toast.error("Remove failed");
    } finally {
      setRemovingId(null);
    }
  };

  /** PREPARE EXPORT DATA */
  const prepareExportData = () => {
    const data = banks.map(bank => ({
      'Bank ID': bank.BankID,
      'Bank Name': bank.BANK_NAME,
      'IFSC Code': bank.ifsc,
      'Alternate Name': bank.bankName || '',
      'Branch Name': bank.branchName || '',
      'Key': bank.key || '',
      'Status': bank.status || 'active',
      'Created At': moment(bank.createdAt).format('DD/MM/YYYY HH:mm:ss'),
      'Updated At': moment(bank.updatedAt).format('DD/MM/YYYY HH:mm:ss')
    }));
    
    setExportData(data);
    return data;
  };

  /** EXPORT TO CSV */
  const handleExportCSV = () => {
    const data = prepareExportData();
    if (data.length === 0) {
      toast.warning("No data to export");
      return;
    }
    
    if (csvLinkRef.current) {
      csvLinkRef.current.link.click();
    }
  };

  /** EXPORT TO EXCEL */
  const handleExportExcel = () => {
    const data = prepareExportData();
    if (data.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Banks");
      
      // Auto-size columns
      const colWidths = data.reduce((widths, row) => {
        Object.keys(row).forEach((key, idx) => {
          const cellLength = String(row[key]).length;
          widths[idx] = Math.max(widths[idx] || 10, cellLength);
        });
        return widths;
      }, []);
      
      worksheet['!cols'] = colWidths.map(w => ({ wch: w + 2 }));
      
      XLSX.writeFile(workbook, `banks_export_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
      toast.success("Excel file downloaded successfully");
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Failed to export Excel file");
    }
  };

  /** HANDLE FILE UPLOAD FOR IMPORT */
  const handleFileUpload = (file) => {
    setImportFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        
        if (jsonData && jsonData.length > 0) {
          setImportPreview(jsonData.slice(0, 5));
        }
      } catch (error) {
        const text = e.target.result;
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        const preview = lines.slice(1, 6).map(line => {
          const values = line.split(',');
          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = values[index] || '';
          });
          return obj;
        });
        setImportPreview(preview);
      }
    };
    
    reader.readAsArrayBuffer(file);
    return false;
  };

  /** PROCESS BULK IMPORT */
  const handleBulkImport = async () => {
    if (!importFile) {
      toast.error("Please select a file first");
      return;
    }

    setImporting(true);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        let jsonData = [];
        
        if (importFile.name.endsWith('.xlsx') || importFile.name.endsWith('.xls')) {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          jsonData = XLSX.utils.sheet_to_json(firstSheet);
        } else {
          const text = e.target.result;
          const lines = text.split('\n');
          const headers = lines[0].split(',').map(h => h.trim());
          jsonData = lines.slice(1).filter(line => line.trim()).map(line => {
            const values = line.split(',');
            const obj = {};
            headers.forEach((header, index) => {
              obj[header] = values[index] ? values[index].trim() : '';
            });
            return obj;
          });
        }

        if (jsonData.length === 0) {
          toast.error("No data found in file");
          setImporting(false);
          return;
        }

        // Transform data with BankID support
        const banksToImport = jsonData.map(row => ({
          BankID: row.BankID || row['Bank ID'] || null,
          BANK_NAME: row.BANK_NAME || row['Bank Name'] || row['BANK NAME'] || '',
          ifsc: row.ifsc || row.IFSC || row.Ifsc || '',
          bankName: row.bankName || row['Bank Name'] || row['Alternate Name'] || '',
          branchName: row.branchName || row['Branch Name'] || row['BRANCH NAME'] || '',
          key: row.key || row.Key || row.KEY || '',
          status: row.status || 'active'
        })).filter(bank => bank.BANK_NAME && bank.ifsc);

        if (banksToImport.length === 0) {
          toast.error("No valid bank data found in file");
          setImporting(false);
          return;
        }

        const res = await postRequest(BULK_IMPORT_BANKS, { banks: banksToImport }, history);
        
        if (res?.success) {
          const successful = res.data?.successful || 0;
          const failed = res.data?.failed || 0;
          
          if (failed === 0) {
            toast.success(`Successfully imported ${successful} banks`);
          } else {
            toast.warning(`Import completed: ${successful} successful, ${failed} failed`);
            
            if (res.data?.errors && res.data.errors.length > 0) {
              console.log("Import errors:", res.data.errors);
            }
          }
          
          setImportModalVisible(false);
          setImportFile(null);
          setImportPreview([]);
          fetchBanks();
        } else {
          toast.error(res?.message || "Import failed");
        }
      } catch (error) {
        toast.error("Import failed");
        console.error("Import error:", error);
      } finally {
        setImporting(false);
      }
    };

    reader.onerror = () => {
      toast.error("Error reading file");
      setImporting(false);
    };

    reader.readAsArrayBuffer(importFile);
  };

  /** RESET ALL FILTERS */
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedKey("");
    setSearchBy("name");
    setPagination({
      current: 1,
      pageSize: 50,
      total: 0,
      pages: 1
    });
  };

  /** TABLE COLUMNS - Showing all fields */
  const columns = [
    {
      name: "Bank ID",
      selector: row => row.BankID,
      sortable: true,
      width: '100px',
      cell: row => (
        <Tag color="blue" style={{ fontWeight: 600, fontSize: '12px' }}>
          #{row.BankID}
        </Tag>
      )
    },
    {
      name: "Main Bank Name",
      selector: row => row.BANK_NAME,
      sortable: true,
      cell: row => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.BANK_NAME}</Text>
        </Space>
      )
    },
    {
      name: "Alternate Name",
      selector: row => row.bankName,
      sortable: true,
      cell: row => row.bankName ? (
        <Text>{row.bankName}</Text>
      ) : (
        <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>
      )
    },
    {
      name: "IFSC Code",
      selector: row => row.ifsc,
      sortable: true,
      width: '140px',
      cell: row => (
        <Tag color="green" style={{ fontWeight: 600, fontSize: '11px' }}>
          {row.ifsc}
        </Tag>
      )
    },
    {
      name: "Branch Name",
      selector: row => row.branchName,
      sortable: true,
      cell: row => row.branchName ? (
        <Text>{row.branchName}</Text>
      ) : (
        <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>
      )
    },
    {
      name: "Key",
      selector: row => row.key,
      sortable: true,
      width: '100px',
      cell: row => row.key ? (
        <Tag color="orange" style={{ fontSize: '11px' }}>{row.key}</Tag>
      ) : (
        <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>
      )
    },
    {
      name: "Status",
      width: '100px',
      cell: row => (
        <Tag 
          color={row.status === "inactive" ? "error" : "success"} 
          icon={row.status === "inactive" ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
          style={{ fontSize: '11px' }}
        >
          {row.status === "inactive" ? "Inactive" : "Active"}
        </Tag>
      )
    },
    // {
    //   name: "Created",
    //   selector: row => row.createdAt,
    //   sortable: true,
    //   width: '120px',
    //   cell: row => (
    //     <Space direction="vertical" size={0} style={{ fontSize: '11px' }}>
    //       <Text type="secondary">{moment(row.createdAt).format('DD/MM/YY')}</Text>
    //       <Text type="secondary">{moment(row.createdAt).format('HH:mm')}</Text>
    //     </Space>
    //   )
    // },
    // {
    //   name: "Updated",
    //   selector: row => row.updatedAt,
    //   sortable: true,
    //   width: '120px',
    //   cell: row => (
    //     <Space direction="vertical" size={0} style={{ fontSize: '11px' }}>
    //       <Text type="secondary">{moment(row.updatedAt).format('DD/MM/YY')}</Text>
    //       <Text type="secondary">{moment(row.updatedAt).format('HH:mm')}</Text>
    //     </Space>
    //   )
    // },
    {
      name: "Actions",
      width: '100px',
      cell: row => (
        <Space>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => openEditModal(row)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>

          <Popconfirm
            title="Delete this bank?"
            description="Are you sure you want to delete this bank?"
            onConfirm={() => handleRemove(row._id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                loading={removingId === row._id}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // CSV headers for export
  const csvHeaders = [
    { label: 'Bank ID', key: 'Bank ID' },
    { label: 'Bank Name', key: 'Bank Name' },
    { label: 'Alternate Name', key: 'Alternate Name' },
    { label: 'IFSC Code', key: 'IFSC Code' },
    { label: 'Branch Name', key: 'Branch Name' },
    { label: 'Key', key: 'Key' },
    { label: 'Status', key: 'Status' },
    { label: 'Created At', key: 'Created At' },
    { label: 'Updated At', key: 'Updated At' }
  ];

  // Initial fetch
  useEffect(() => {
    fetchBanks();
    fetchUniqueKeys();
  }, []);

  // Fetch when pagination or filters change
  useEffect(() => {
    fetchBanks();
  }, [pagination.current, pagination.pageSize, searchTerm, selectedKey]);

  return (
    <div
      style={{
        background: "#ffffff",
        minHeight: "100vh",
        padding: 0
      }}
    >
      <div style={{ maxWidth: 1600, margin: "0 auto" }}>
        {/* HEADER WITH ACTION BUTTONS */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,.06)",
            marginBottom: 24
          }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={12}>
              <Space direction="vertical" size={0}>
                <Title level={4} style={{ margin: 0, color: "#1a1a1a" }}>
                  <BankOutlined /> Bank Management
                </Title>
                <Text type="secondary">
                  {pagination.total} banks found • Page {pagination.current} of {pagination.pages}
                </Text>
              </Space>
            </Col>
            <Col xs={24} md={12}>
              <Space style={{ width: "100%", justifyContent: "flex-end" }} wrap>
                <Button
                  icon={<ImportOutlined />}
                  size="large"
                  onClick={() => setImportModalVisible(true)}
                  style={{ borderRadius: 12 }}
                >
                  Bulk Import
                </Button>
                
                <Button
                  icon={<FileExcelOutlined />}
                  size="large"
                  onClick={handleExportExcel}
                  style={{ borderRadius: 12 }}
                >
                  Export Excel
                </Button>
                
                <Button
                  icon={<DownloadOutlined />}
                  size="large"
                  onClick={handleExportCSV}
                  style={{ borderRadius: 12 }}
                >
                  Export CSV
                </Button>
                
                <Button
                  icon={<PlusOutlined />}
                  type="primary"
                  size="large"
                  onClick={openAddModal}
                  style={{
                    borderRadius: 12,
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    border: "none",
                    fontWeight: 600
                  }}
                >
                  Add New Bank
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* SEARCH AND FILTER CARD */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,.06)",
            marginBottom: 24
          }}
        >
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} md={4}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>Search Type</Text>
                <Select
                  value={searchBy}
                  onChange={setSearchBy}
                  style={{ width: "100%" }}
                  size="large"
                >
                  <Option value="name">Bank Name</Option>
                  <Option value="ifsc">IFSC Code</Option>
                  <Option value="key">Key</Option>
                  <Option value="bankid">Bank ID</Option>
                </Select>
              </Space>
            </Col>
            
            <Col xs={24} md={6}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>Search Term</Text>
                <Input
                  placeholder={`Search by ${searchBy}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="large"
                  onPressEnter={handleSearch}
                  prefix={<SearchOutlined />}
                  style={{ borderRadius: 12 }}
                />
              </Space>
            </Col>
            
            <Col xs={24} md={6}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>Filter by Key</Text>
                <Select
                  placeholder="All Banks"
                  value={selectedKey}
                  onChange={handleFilterByKey}
                  style={{ width: "100%" }}
                  size="large"
                  allowClear
                >
                  {uniqueKeys.map(key => (
                    <Option key={key} value={key}>
                      <Tag color="orange">{key}</Tag>
                    </Option>
                  ))}
                </Select>
              </Space>
            </Col>
            
            <Col xs={24} md={8}>
              <Space style={{ width: "100%", justifyContent: "flex-end", marginTop: 28 }}>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  size="large"
                  style={{ borderRadius: 12, height: 40 }}
                >
                  Search
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleResetFilters}
                  size="large"
                  style={{ borderRadius: 12, height: 40 }}
                >
                  Reset
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* MAIN TABLE CARD */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,.06)"
          }}
          bodyStyle={{ padding: 0 }}
        >
          <div style={{ padding: '16px 24px 0 24px' }}>
            <Space style={{ justifyContent: 'space-between', width: '100%', marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0 }}>Bank List</Title>
              <Badge 
                count={banks.length} 
                style={{ backgroundColor: '#52c41a' }}
                showZero 
              />
            </Space>
          </div>
          
          <DataTable
            columns={columns}
            data={banks}
            progressPending={loading}
            highlightOnHover
            pagination
            paginationServer
            paginationTotalRows={pagination.total}
            paginationDefaultPage={pagination.current}
            paginationPerPage={pagination.pageSize}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            onChangePage={handleTableChange}
            onChangeRowsPerPage={handlePerRowsChange}
            noDataComponent={
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text type="secondary">
                    No banks found. Add your first bank using the "Add New Bank" button.
                  </Text>
                }
                style={{ padding: "40px 0" }}
              />
            }
            customStyles={{
              headRow: {
                style: {
                  backgroundColor: "#f8fafc",
                  borderBottom: "2px solid #e2e8f0",
                  fontSize: '12px',
                  fontWeight: 600
                },
              },
              rows: {
                style: {
                  fontSize: "13px",
                  "&:hover": {
                    backgroundColor: "#f1f5f9 !important",
                  },
                },
              },
              cells: {
                style: {
                  padding: '12px 8px',
                }
              }
            }}
          />
        </Card>

        {/* Hidden CSV Download Link */}
        <CSVLink
          ref={csvLinkRef}
          data={exportData}
          headers={csvHeaders}
          filename={`banks_export_${moment().format('YYYYMMDD_HHmmss')}.csv`}
          style={{ display: 'none' }}
        />

        {/* ADD/EDIT BANK MODAL */}
        <Modal
          title={
            <Space>
              <BankOutlined />
              <Text strong>{modalTitle}</Text>
            </Space>
          }
          open={modalVisible}
          onCancel={closeModal}
          footer={null}
          width={700}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            initialValues={{ status: 'active' }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="BankID"
                  label={
                    <Space>
                      <NumberOutlined />
                      <span>Bank ID {!editingBank && <Text type="secondary">(Optional)</Text>}</span>
                    </Space>
                  }
                  rules={[
                    { 
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        if (isNaN(value)) return Promise.reject('Bank ID must be a number');
                        if (value <= 0) return Promise.reject('Bank ID must be a positive number');
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Input
                    placeholder="Auto-generated if left empty"
                    size="large"
                    prefix={<NumberOutlined />}
                    disabled={editingBank} // Cannot change BankID for existing banks
                    type="number"
                    min={1}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="BANK_NAME"
                  label={
                    <Space>
                      <BankOutlined />
                      <span>Bank Name</span>
                    </Space>
                  }
                  rules={[
                    { required: true, message: "Please enter bank name" },
                    { max: 100, message: "Bank name cannot exceed 100 characters" }
                  ]}
                >
                  <Input
                    placeholder="Enter bank name"
                    size="large"
                    style={{ textTransform: "uppercase" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="ifsc"
                  label={
                    <Space>
                      <IdcardOutlined />
                      <span>IFSC Code</span>
                    </Space>
                  }
                  rules={[
                    { required: true, message: "Please enter IFSC code" },
                    { 
                      pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/, 
                      message: "Invalid IFSC format. Example: SBIN0001234" 
                    }
                  ]}
                >
                  <Input
                    placeholder="SBIN0001234"
                    size="large"
                    style={{ textTransform: "uppercase" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="branchName"
                  label={
                    <Space>
                      <BranchesOutlined />
                      <span>Branch Name</span>
                    </Space>
                  }
                  rules={[{ max: 100, message: "Branch name cannot exceed 100 characters" }]}
                >
                  <Input
                    placeholder="Enter branch name"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="bankName"
                  label="Alternate Bank Name"
                  rules={[{ max: 100, message: "Alternate name cannot exceed 100 characters" }]}
                >
                  <Input
                    placeholder="Enter alternate bank name (optional)"
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="key"
                  label={
                    <Space>
                      <KeyOutlined />
                      <span>Key</span>
                    </Space>
                  }
                  rules={[{ max: 50, message: "Key cannot exceed 50 characters" }]}
                >
                  <Input
                    placeholder="Enter key (optional)"
                    size="large"
                    style={{ textTransform: "uppercase" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="status" label="Status">
              <Select size="large">
                <Option value="active">
                  <Tag color="success">Active</Tag>
                </Option>
                <Option value="inactive">
                  <Tag color="error">Inactive</Tag>
                </Option>
              </Select>
            </Form.Item>

            {editingBank && (
              <Alert
                message="Note"
                description="Bank ID cannot be changed for existing records."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Divider />

            <Form.Item style={{ marginBottom: 0 }}>
              <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                <Button onClick={closeModal} size="large" style={{ borderRadius: 8 }}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={actionLoading}
                  size="large"
                  style={{
                    borderRadius: 8,
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    border: "none",
                    fontWeight: 600
                  }}
                >
                  {editingBank ? "Update Bank" : "Add Bank"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* BULK IMPORT MODAL */}
        <Modal
          title={
            <Space>
              <ImportOutlined />
              <Text strong>Bulk Import Banks</Text>
            </Space>
          }
          open={importModalVisible}
          onCancel={() => {
            setImportModalVisible(false);
            setImportFile(null);
            setImportPreview([]);
          }}
          footer={null}
          width={800}
          destroyOnClose
        >
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Alert
              message="Import Instructions"
              description={
                <div>
                  <Text strong>Required Columns:</Text>
                  <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                    <li>BANK_NAME (or Bank Name)</li>
                    <li>ifsc (or IFSC)</li>
                  </ul>
                  <Text strong>Optional Columns:</Text>
                  <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                    <li>BankID (numeric, will be auto-generated if empty)</li>
                    <li>bankName (Alternate Name)</li>
                    <li>branchName (Branch Name)</li>
                    <li>key</li>
                    <li>status (default: active)</li>
                  </ul>
                </div>
              }
              type="info"
              showIcon
            />
            
            <Upload.Dragger
              accept=".csv,.xlsx,.xls"
              beforeUpload={handleFileUpload}
              showUploadList={false}
              disabled={importing}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Support for CSV or Excel files
              </p>
            </Upload.Dragger>
            
            {importFile && (
              <Alert
                message={`File selected: ${importFile.name}`}
                description={`Size: ${(importFile.size / 1024).toFixed(2)} KB`}
                type="success"
                showIcon
              />
            )}
            
            {importPreview.length > 0 && (
              <div>
                <Text strong>Preview (first 5 rows):</Text>
                <div style={{ maxHeight: 200, overflow: 'auto', marginTop: 10 }}>
                  <Table
                    dataSource={importPreview}
                    columns={Object.keys(importPreview[0] || {}).map(key => ({
                      title: key,
                      dataIndex: key,
                      key: key,
                      render: (text) => text || '-',
                      ellipsis: true
                    }))}
                    size="small"
                    pagination={false}
                    scroll={{ x: true }}
                  />
                </div>
              </div>
            )}
            
            {importing && (
              <div style={{ textAlign: 'center' }}>
                <Text strong>Importing banks...</Text>
                <br />
                <Text type="secondary">Please wait while we process your file</Text>
              </div>
            )}
            
            <Divider />
            
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setImportModalVisible(false);
                  setImportFile(null);
                  setImportPreview([]);
                }}
                disabled={importing}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<ImportOutlined />}
                onClick={handleBulkImport}
                loading={importing}
                disabled={!importFile || importing}
              >
                {importing ? 'Importing...' : 'Start Import'}
              </Button>
            </Space>
          </Space>
        </Modal>
      </div>
    </div>
  );
};

export default BankManagementPage;